<template>
  <!-- Tool Detail Drawer (nested) -->
  <ToolDetailDrawer
    v-model="toolDrawerVisible"
    :software-name="selectedSoftwareName" />

  <SmDrawer
    v-model:visibility="localVisibility"
    :title="`${i18n.t('browserExtension.person')} - ${truncatedInstallationId}`"
    class="person-drawer">
    <template #close>
      <SmButtonClose @close="localVisibility = false" />
    </template>

    <!-- Loading state when installationId is not yet available -->
    <div
      v-if="!props.installationId"
      class="flex h-full items-center justify-center">
      <SmSkeleton :count="5" />
    </div>

    <!-- Tabs content - only render when installationId is available -->
    <el-tabs
      v-else
      v-model="activeTab"
      class="person-drawer-tabs"
      @tab-change="handleTabChange">
      <!-- Overview Tab -->
      <el-tab-pane
        :label="capitalize(i18n.t('overview'))"
        name="overview"
        lazy
        class="person-drawer-pane">
        <PersonDrawerOverview
          :installation-id="props.installationId"
          :from-date="dateRange?.[0]"
          :till-date="dateRange?.[1]"
          @tool-click="handleToolClick" />
      </el-tab-pane>

      <!-- Tools Tab -->
      <el-tab-pane
        :label="i18n.t('browserExtension.tools')"
        name="tools"
        lazy
        class="person-drawer-pane">
        <PersonDrawerTools
          :installation-id="props.installationId"
          :from-date="dateRange?.[0]"
          :till-date="dateRange?.[1]"
          @tool-click="handleToolClick" />
      </el-tab-pane>

      <!-- Activity Timeline Tab -->
      <el-tab-pane
        :label="i18n.t('browserExtension.activityTimeline')"
        name="activity"
        lazy
        class="deep-contain-none-important person-drawer-pane">
        <PersonDrawerActivity
          :installation-id="props.installationId"
          :from-date="dateRange?.[0]"
          :till-date="dateRange?.[1]"
          @tool-click="handleToolClick" />
      </el-tab-pane>
    </el-tabs>

    <!-- Date Range Picker for Drawer -->
    <template #footer>
      <div class="flex items-center gap-4">
        <SmDatepicker
          v-model:daterange="dateRange"
          is-range
          is-dropdown
          :min-date="null"
          :quick-options="dateRangePresets"
          :placeholder="i18n.t('browserExtension.selectDateRange')"
          dropdown-class="w-[30rem] max-w-[calc(100vw-2rem)]"
          class="flex-1">
          <template #trigger>
            <CalendarIcon class="mr-4 text-contrast-muted" :size="20" />
            <span
              class="flex-1 truncate"
              :class="{ 'text-contrast-muted': !hasSelectedDateRange }">
              {{ dateRangeTriggerLabel }}
            </span>
          </template>
        </SmDatepicker>
      </div>
    </template>
  </SmDrawer>
</template>

<script setup lang="ts">
  import { computed, ref, provide, toRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import dayjs from 'dayjs'
  import { Calendar as CalendarIcon } from 'lucide-vue-next'
  import SmDrawer from '@/components/sm/SmDrawer.vue'
  import SmButtonClose from '@/components/sm/SmButtonClose.vue'
  import SmDatepicker from '@/components/sm/SmDatepicker.vue'
  import SmSkeleton from '@/components/sm/SmSkeleton.vue'
  import PersonDrawerOverview from './person/PersonOverview.vue'
  import PersonDrawerTools from './person/PersonTools.vue'
  import PersonDrawerActivity from './person/PersonActivity.vue'
  import ToolDetailDrawer from './ToolDetailDrawer.vue'
  import { capitalize } from '@/utils/formatter/formatter'
  import { useTabPersistence } from '@/composables/useTabPersistence'
  import { useDateRangeFilter } from '@/composables/useDateRangeFilter'
  import {
    usePersonStats,
    PERSON_STATS_KEY,
  } from '@/stores/browserextension/browserextension.store'
  import { truncateId } from '@/stores/browserextension/utils'
  import { displayDate } from '@/utils/formatter/date'

  interface Props {
    visibility: boolean
    installationId: string | null
  }

  interface Emits {
    (e: 'update:visibility', value: boolean): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const i18n = useI18n()

  // Tool drawer state (for nested tool detail view)
  const toolDrawerVisible = ref(false)
  const selectedSoftwareName = ref<string | null>(null)

  // Handle tool click from child components
  const handleToolClick = (toolName: string) => {
    selectedSoftwareName.value = toolName
    toolDrawerVisible.value = true
  }

  // Local drawer visibility
  const localVisibility = computed({
    get: () => props.visibility,
    set: (value) => emit('update:visibility', value),
  })

  // Use Composable for Tab Persistence
  const { activeTab, handleTabChange } = useTabPersistence(
    'person-drawer-active-tab',
    'overview',
    ['overview', 'tools', 'activity']
  )

  // Use Composable for Date Range
  const { dateRange, dateRangePresets } = useDateRangeFilter(30)

  const hasSelectedDateRange = computed(function () {
    return !!(dateRange.value && dateRange.value[0] && dateRange.value[1])
  })

  function isSameDateRange(
    startA: Date | null | undefined,
    endA: Date | null | undefined,
    startB: Date | null | undefined,
    endB: Date | null | undefined
  ) {
    if (!startA || !endA || !startB || !endB) {
      return false
    }

    return (
      dayjs(startA).isSame(startB, 'day') && dayjs(endA).isSame(endB, 'day')
    )
  }

  const selectedQuickOptionLabel = computed(function () {
    if (!dateRange.value || !dateRange.value[0] || !dateRange.value[1]) {
      return null
    }

    for (const option of dateRangePresets.value) {
      if (
        isSameDateRange(
          dateRange.value[0],
          dateRange.value[1],
          option.start,
          option.end
        )
      ) {
        return option.label
      }
    }

    return null
  })

  const dateRangeTriggerLabel = computed(function () {
    if (!dateRange.value || !dateRange.value[0] || !dateRange.value[1]) {
      return i18n.t('browserExtension.selectDateRange')
    }

    if (selectedQuickOptionLabel.value) {
      return selectedQuickOptionLabel.value
    }

    return (
      displayDate(dateRange.value[0], { dateStyle: 'medium' }) +
      ' - ' +
      displayDate(dateRange.value[1], { dateStyle: 'medium' })
    )
  })

  // Fetch shared person stats once and provide to children
  const installationIdRef = toRef(props, 'installationId')
  const fromDateRef = computed(() => dateRange.value?.[0] ?? null)
  const tillDateRef = computed(() => dateRange.value?.[1] ?? null)

  const personStatsContext = usePersonStats(
    installationIdRef,
    fromDateRef,
    tillDateRef
  )
  provide(PERSON_STATS_KEY, personStatsContext)

  // Truncate installation ID for drawer title
  const truncatedInstallationId = computed(() =>
    truncateId(props.installationId ?? '', 8, 0)
  )
</script>

<style scoped>
  .person-drawer :deep(.sm-drawer__body) {
    overflow: hidden;
  }

  .person-drawer-tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .person-drawer-tabs :deep(.el-tabs__header) {
    flex-shrink: 0;
    margin: 0;
    order: 0;
  }

  .person-drawer-tabs :deep(.el-tabs__content) {
    flex: 1;
    height: 0;
    overflow: hidden;
    order: 1;
  }

  .person-drawer-pane {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }
</style>

<style lang="css">
  .deep-contain-none-important * {
    contain: none !important;
  }
</style>
